/* eslint-disable no-nested-ternary */
import React from 'react';
import { Badge, Button } from 'react-bootstrap';
import { BOOKING_TYPE_LABELS, operatorRoleBadge } from '../../../config/bookingConstants';
import ProgressStrip from '../../Portal/Components/ProgressStrip';
import {
  flightBadge, passengerSummary, place, programmeHours, programmeName, programmeStops,
  progressIndex,
} from '../../Portal/portalFormat';
import {
  FLIGHT_COPY_EN, PASSENGER_WORDS_EN, PROGRESS_STEPS_EN, ROUTE_MAP_COPY_EN,
} from '../../Portal/Operator/operatorFormat';
import { CLOSED_BY_OFFICE, stepText } from '../../Portal/Dispatcher/dispatcherFormat';
import {
  assignmentsOf, crewOf, driverIsOffApp, driverProgress, needsDriver, progressDriver,
} from '../tripProgress';
import {
  barLook, personName, tripClock, tripWindow,
} from '../dayTimeline';

function getStatusBadgeBg(status) {
  switch (status) {
    case 'Confirmed': return 'success';
    case 'Completed': return 'primary';
    case 'Cancelled': return 'danger';
    case 'Request': return 'warning';
    default: return 'secondary';
  }
}

/**
 * 'to ~14:35 (estimated)' — or, for a programme, 'to 17:00': its hours were
 * sold, so its end is a fact rather than a guess. Both are the API's.
 */
const endText = (trip, { end }) => {
  if (end === null) return 'End not estimated';
  return trip.estimated_end.basis === 'programme'
    ? `to ${tripClock(trip, end)}`
    : `to ~${tripClock(trip, end)} (estimated)`;
};

/** The line under the route: the programme's booked hours, or the estimated drive. */
const routeMeta = (trip) => {
  const estimate = trip.estimated_end;

  if (estimate?.basis === 'programme') {
    const hours = programmeHours(trip);
    return [programmeName(trip), hours ? ROUTE_MAP_COPY_EN.programmeDuration(hours) : '']
      .filter(Boolean).join(' · ');
  }

  const drive = ROUTE_MAP_COPY_EN.duration(estimate?.drive_minutes);
  return drive ? `Drive ${drive} (estimated)` : '';
};

/**
 * What the office should act on, loudest first: the driver's own flags
 * (`driverProgress` — the dispatch board's words), then a gap in who is on
 * the trip. `tone` is the board's: bad or warn. A return row has no
 * `progress` (see dayTimeline's `tripWindow`), so only the gap can show.
 */
const alertsFor = (trip, progress, hasOperator) => {
  const { attention, late, step } = progress || {};
  const alerts = [];
  const pickup = tripClock(trip, trip.date_datetime);

  if (attention?.key === 'late') {
    alerts.push({ tone: 'bad', title: late || attention.label, text: `Pickup was ${pickup} · ${stepText(step)}` });
  } else if (attention?.key === 'waiting') {
    alerts.push({ tone: 'warn', title: attention.label, text: `${stepText(step)} · passenger not on board yet` });
  } else if (attention?.key === 'no_show') {
    alerts.push({
      tone: 'bad',
      title: attention.label,
      text: trip.status === 'Confirmed'
        ? `${stepText(step)}. The booking is still Confirmed — set its status once it has been dealt with.`
        : `Recorded as ${trip.status}.`,
    });
  }

  if (!hasOperator) {
    alerts.push({ tone: 'bad', title: 'No operator assigned', text: 'Assign an operator so a driver can be named.' });
  } else if (needsDriver(trip)) {
    alerts.push({ tone: 'warn', title: 'No driver named', text: 'Assign a driver so somebody is on this trip.' });
  }

  return alerts;
};

/** 'Kovács Béla also has Smith at 14:15 (to ~14:50). They overlap by ~20 min.' */
const overlapText = (trip, overlap) => {
  const other = overlap.a === trip ? overlap.b : overlap.a;
  const shared = assignmentsOf(trip).find((entry) => entry.driverId === overlap.driverId);
  const driver = personName(shared?.driver) || 'The same driver';
  const minutes = Math.round((overlap.to - overlap.from) / 60000);

  return `${driver} also has ${other.full_name || 'another trip'} at ${tripClock(other, other.date_datetime)} `
    + `(to ~${tripClock(other, tripWindow(other).end)}). They overlap by ~${minutes} min.`;
};

/** Where the driver has got to — or why nobody can tell us. */
function DriverProgress({
  trip, progress, hasOperator, now,
}) {
  const driver = progressDriver(trip);

  if (!progress) {
    return (
      <p className="dov-panel__note">
        This is the return leg. Driver progress is only recorded for the outbound trip,
        so none is shown here.
      </p>
    );
  }
  if (!hasOperator) return <p className="dov-panel__note">No operator yet, so nobody can report progress.</p>;
  if (!driver) return <p className="dov-panel__note">No driver named on this booking, so nobody is reporting progress.</p>;
  if (driverIsOffApp(trip)) {
    return (
      <p className="dov-panel__note">
        {`${personName(driver) || 'The driver'} isn't using the driver app, so progress won't show here.`}
      </p>
    );
  }
  // Past its pickup, nothing tapped, not called late — an experience, or a
  // driver the API did not say is on the app. The bar says "progress not
  // visible"; this says why, rather than "Not started".
  if (barLook(trip, now) === 'off-app') {
    return (
      <p className="dov-panel__note">
        {trip.booking_type === 'experience'
          ? 'No progress recorded. An experience is often run by a guide who doesn\'t use the driver app, so it is never marked late.'
          : `No progress recorded. It isn't known whether ${personName(driver) || 'the driver'} uses the driver app, so the trip isn't marked late.`}
      </p>
    );
  }

  const { step, showsStrip } = progress;

  return (
    <div className="dov-progress">
      {showsStrip && (
        <ProgressStrip
          index={progressIndex(trip)}
          steps={PROGRESS_STEPS_EN}
          label={`Driver progress: ${step.label}`}
        />
      )}
      <span className={`dov-step dov-step--${step.tone}`}>{stepText(step)}</span>
      {step.closedByOffice && <span className="dov-step dov-step--muted">{CLOSED_BY_OFFICE}</span>}
    </div>
  );
}

/** The flight the API looked up with the estimate, else just its number. */
function Flight({ trip }) {
  const status = trip.estimated_end?.flight || null;
  const number = status?.flight_number || trip.flight_number;
  if (!number) return null;

  // `flightBadge` reads the shape the driver portal's `flight_status` has,
  // which is the shape the API sends here.
  const badge = flightBadge({ flight_status: status }, FLIGHT_COPY_EN);
  const times = status ? [
    [FLIGHT_COPY_EN.arrivalAirport, status.arrival_airport],
    [FLIGHT_COPY_EN.scheduled, tripClock(trip, status.scheduled_arrival)],
    [FLIGHT_COPY_EN.expected, tripClock(trip, status.expected_arrival)],
    [FLIGHT_COPY_EN.landed, tripClock(trip, status.actual_arrival)],
  ].filter(([, value]) => Boolean(value)) : [];

  return (
    <section className="dov-flight" aria-label={FLIGHT_COPY_EN.title}>
      <div className="dov-flight__head">
        <i className="fa fa-plane" aria-hidden="true" />
        <span className="dov-flight__number">{number}</span>
        {badge && <span className={`dov-flight__badge dov-flight__badge--${badge.tone}`}>{badge.text}</span>}
      </div>
      {times.length > 0 && (
        <dl className="dov-flight__times">
          {times.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/**
 * The booking behind the bar that was clicked: what it is, what is wrong
 * with it, where the driver has got to, and the same actions the trip card
 * used to carry.
 */
function TripPanel({
  trip,
  leg,
  now,
  overlaps,
  onClose,
  onEditBooking,
  onAssignOperator,
  onAssignDriver,
  onViewDetails,
  onViewDriverEvents,
  onSearchLinkedBookings,
}) {
  const typeInfo = BOOKING_TYPE_LABELS[trip.booking_type] || BOOKING_TYPE_LABELS.transfer;
  const hasOperator = crewOf(trip).length > 0;
  const hasDriver = Boolean(progressDriver(trip));
  const progress = leg.isReturn ? null : driverProgress(trip, now);
  const alerts = alertsFor(trip, progress, hasOperator);
  const stops = programmeStops(trip).map((stop) => place(stop).title).filter(Boolean);
  const meta = [
    passengerSummary(trip, PASSENGER_WORDS_EN),
    trip.booking_type !== 'experience' ? trip.category?.name : '',
  ].filter(Boolean).join(' · ');
  const linked = trip.linked_booking_ids?.length || 0;
  const assignments = assignmentsOf(trip);
  const routeLine = routeMeta(trip);

  return (
    <aside className="dov-panel" aria-label="Selected trip">
      <div className="dov-panel__top">
        <div className="dov-panel__badges">
          <Badge bg="light" text="dark" className="border">
            <i className={`fa ${typeInfo.icon} me-1`} />
            {typeInfo.label}
          </Badge>
          <Badge bg={getStatusBadgeBg(trip.status)}>{trip.status || 'Unknown'}</Badge>
          {leg.isReturn && <Badge bg="light" text="dark" className="border">Return leg</Badge>}
          {trip.booking_reference && <span className="dov-panel__ref">{trip.booking_reference}</span>}
        </div>
        <button type="button" className="dov-panel__close" onClick={onClose} aria-label="Close">
          <i className="fa fa-times" />
        </button>
      </div>

      <div>
        <div className="dov-panel__when">
          <span className="dov-panel__time">{tripClock(trip, leg.start) || '--:--'}</span>
          <span className="dov-panel__end">{endText(trip, leg)}</span>
        </div>
        <div className="dov-panel__name">{trip.full_name || '—'}</div>
        {meta && <div className="dov-panel__meta">{meta}</div>}
      </div>

      {alerts.map((alert) => (
        <div key={alert.title} role="status" className={`dov-alert dov-alert--${alert.tone}`}>
          <i className="fa fa-exclamation-triangle" aria-hidden="true" />
          <div>
            <strong>{alert.title}</strong>
            <span>{alert.text}</span>
          </div>
        </div>
      ))}

      {overlaps.map((overlap) => (
        <div key={`${overlap.driverId}-${overlap.from}`} role="status" className="dov-alert dov-alert--overlap">
          <i className="fa fa-clone" aria-hidden="true" />
          <div>
            <strong>Same driver, overlapping</strong>
            <span>{overlapText(trip, overlap)}</span>
          </div>
        </div>
      ))}

      <section className="dov-panel__section">
        <h3 className="dov-panel__heading">Driver progress</h3>
        <DriverProgress trip={trip} progress={progress} hasOperator={hasOperator} now={now} />
      </section>

      <section className="dov-panel__section">
        <h3 className="dov-panel__heading">Route</h3>
        <ol className="dov-route">
          <li className="dov-route__from">{trip.destination_from?.address || '—'}</li>
          {stops.length > 0 && <li className="dov-route__via">{`Stops: ${stops.join(', ')}`}</li>}
          <li className="dov-route__to">{trip.destination_to?.address || '—'}</li>
        </ol>
        {routeLine && <div className="dov-panel__meta">{routeLine}</div>}
      </section>

      <Flight trip={trip} />

      <section className="dov-panel__section">
        <h3 className="dov-panel__heading">Assigned</h3>
        {assignments.length ? assignments.map((entry) => {
          const role = operatorRoleBadge(entry.role, entry.operator);
          const appLink = entry.driver?.has_app_link;
          return (
            <div key={`${entry.role}-${entry.operatorId}-${entry.driverId}`} className="dov-assignment">
              <div>
                <strong>{entry.operator?.name || 'Unassigned'}</strong>
                <Badge bg={role.color} className="ms-2">{role.label}</Badge>
              </div>
              <div>
                {[
                  personName(entry.driver) || (entry.role === 'driver' ? 'No driver named' : ''),
                  entry.driver?.phone_number,
                ].filter(Boolean).join(' · ')}
              </div>
              <div className="dov-panel__meta">
                {[
                  entry.car?.plate_number || entry.car?.type,
                  appLink === true ? 'on the app' : appLink === false ? 'not on the app' : '',
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
          );
        }) : <p className="dov-panel__note">Nobody is assigned yet.</p>}
        {linked > 0 && onSearchLinkedBookings && (
          <Button size="sm" variant="outline-info" onClick={() => onSearchLinkedBookings(trip)}>
            <i className="fa fa-link me-1" />
            {`${linked} linked`}
          </Button>
        )}
      </section>

      <div className="dov-panel__actions">
        <Button size="sm" variant="outline-secondary" onClick={() => onViewDetails(trip)}>
          <i className="fa fa-eye me-1" />
          Details
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={() => onEditBooking(trip)}>
          <i className="fa fa-edit me-1" />
          Edit booking
        </Button>
        <Button size="sm" variant={hasOperator ? 'outline-secondary' : 'dark'} onClick={() => onAssignOperator(trip)}>
          <i className="fa fa-building me-1" />
          {hasOperator ? 'Change operator' : 'Assign operator'}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={() => onAssignDriver(trip)} disabled={!hasOperator}>
          <i className="fa fa-car me-1" />
          {hasDriver ? 'Change driver' : 'Assign driver'}
        </Button>
        {hasDriver && (
          <Button size="sm" variant="outline-secondary" className="dov-panel__wide" onClick={() => onViewDriverEvents(trip)}>
            <i className="fa fa-history me-1" />
            Driver events
          </Button>
        )}
      </div>
    </aside>
  );
}

export default TripPanel;
